import { apiClass } from '../../class/api.class'
import { MatomoClass } from '@/base/class/matomo.class'
import { ENDPOINTS } from '../../const/endpoints.const'
import { PAYMENT_CLIENT_SOURCE } from '@/base/const/payment.const'
import { LANG_DEFAULT, LANGUAGES_ACTIVE_LIST } from '@/base/const/lang.const'
import {
  CARD_ID_LIST,
  PAYWAY_GROUP_MODEL,
  PAYWAY_GROUPS_FRONT_API_MAP,
  PAYWAY_GROUPS_TYPES,
  paywayGroupsList,
} from '@/base/const/payway.const'
import { ToolsClass } from '@/base/class/tools.class'
import { PAYWALL_MODE, PAYWALL_PAYWAY_METHOD, PAYWALL_THEME } from '@/base/const/paywall.const'
import { CookieClass } from '@/base/class/cookie.class'
import { COOKIE_LANG } from '@/base/const/cookies.const'
import { REGULATION_TYPE } from '@/base/const/regulation.const'
import { PROMOTION_TYPES } from '@/base/const/promotion.const'
import { HTTP_STATUS } from '@/base/const/http.const'
import { PaymentBoost } from '@/base/class/paymentBoost.class'
import { isPaymentFeeActive, isPaymentFeeDifferent, sortPaymentGroups } from '@/base/domain/paymentFee.domain'
import { getTransactionAmount, getTransactionBaseAmount } from '@/base/domain/paymentAmount.domain'
import { PAYMENT_FEE_SID_CONFIG } from '@/base/const/paymentFee.const'
import { reportMessage, REPORT_LEVEL } from '@/base/utils/errorReporting'
import { identifyTransaction, trackStep } from '@/base/utils/transactionTracking'
import { TRX_STEP } from '@/base/const/transactionTracking.const'
import { MATOMO_PAYMENT_TYPE_GROUP } from '@/base/const/matomo.const'
import { getBrowserData } from '@/base/utils/browserData'
import { extractBlikDigits } from '@/base/utils/blikCode'
import { getPreferredWalletGroupType, orderWalletGroups } from '@/base/domain/walletOrder.domain'
import { WALLET_ORDER_VARIANT } from '@/base/const/walletOrder.const'

// Źródła, które kończą przepływ w `Payment/Loader/Loading.vue` (`PaymentData`/`PaymentPsd2Data`) -
// ta sama lista co w `useLoading.isPaymentDataSet`, bez `visaMobileData` (ma własny komponent w widoku).
const LOADER_CLIENT_SOURCE_LIST = [
  PAYMENT_CLIENT_SOURCE.pgData,
  PAYMENT_CLIENT_SOURCE.pbcData,
  PAYMENT_CLIENT_SOURCE.pblData,
  PAYMENT_CLIENT_SOURCE.pbiData,
  PAYMENT_CLIENT_SOURCE.walletData,
  PAYMENT_CLIENT_SOURCE.psd2Data,
]

const state = {
  loading: false,
  processing: false,
  selectOther: false,
  apiToken: null,
  transRemoteID: null,
  continueHash: null,
  contextName: null,
  paymentData: null,
  paywaysLanguageData: null,
  selectedPaywayGroupType: null,
  selectedPaywayGroupId: null,
  paywallTheme: PAYWALL_THEME.light,
  paywallView: false,
  paywallMode: PAYWALL_MODE.main,
  paywayMethod: PAYWALL_PAYWAY_METHOD.single,
  form: {
    serviceID: null,
    paywayID: null,
    transRemoteID: null,
    customerEmail: null,
    blikCode: null,
    regulation: null,
    regulationAcceptanceId: null,
    regulationApproved: null,
    recurringAcceptanceStateAfter: null,
    walletType: null,
    visaMobile: null,
    widgetData: {
      bmUserId: null,
      bmUserIdHash: null,
    },
  },
  error: {
    invalidFields: null,
  },
  session: {
    active: true,
    refreshing: null,
    currentTimestamp: +new Date(),
  },
  rateLimitExceeded: false,
  reloadActivePayway: null,
  isPaymentBoostActive: false,
  isPaymentBoostGroupsHidden: false,
  payerPaywallHistory: null,
  payerPaywallReturn: null,
  payerPaywallCompleted: false,
  // PEP-1298: wariant eksperymentu Matomo `KolejnoscPortfeli`; null = eksperyment nie ruszył
  walletOrderVariant: null,
}

const mutations = {
  SET_PAYMENT_LOADING(state, value) {
    state.loading = value
  },
  SET_PAYMENT_PROCESSING(state, value) {
    state.processing = value
  },
  SET_PAYMENT_API_TOKEN(state, value) {
    state.apiToken = value
  },
  SET_PAYMENT_CONTEXT_NAME(state, value) {
    state.contextName = value
  },
  SET_PAYMENT_FORM_VISAMOBILE(state, value) {
    state.form.visaMobile = value
  },
  SET_PAYMENT_FORM_EMAIL(state, value) {
    state.form.customerEmail = value
  },
  SET_PAYMENT_FORM_BLIK_CODE(state, value) {
    state.form.blikCode = value
  },
  SET_PAYMENT_FORM_PAYWAY_ID(state, value) {
    state.form.paywayID = value
  },
  SET_PAYMENT_FORM_REGULATION_ID(state, value) {
    state.form.regulationAcceptanceId = value
  },
  SET_PAYMENT_FORM_REGULATION(state, data) {
    state.form.regulation = data
  },
  SET_PAYMENT_FORM_REGULATION_APPROVED(state, value) {
    state.form.regulationApproved = value
  },
  SET_PAYMENT_FORM_RECURRING_ACCEPTANCE_STATE_AFTER(state, value) {
    state.form.recurringAcceptanceStateAfter = value
  },
  SET_PAYMENT_FORM_WALLET_TYPE(state, value) {
    state.form.walletType = value
  },
  SET_PAYMENT_FORM_SERVICE_ID(state, value) {
    state.form.serviceID = value
  },
  SET_PAYMENT_FORM_TRANS_REMOTE_ID(state, value) {
    state.form.transRemoteID = value
  },
  SET_PAYMENT_DATA(state, data) {
    state.paymentData = Object.assign({}, state.paymentData || {}, data)
  },
  SET_PAYWAYS_LANGUAGE_DATA(state, data) {
    state.paywaysLanguageData = data
  },
  SET_PAYMENT_TRANS_REMOTE_ID(state, value) {
    state.transRemoteID = value
  },
  SET_PAYMENT_CONTINUE_HASH(state, value) {
    state.continueHash = value
  },
  SET_PAYMENT_TRANSACTION_PAYWAY_GROUPS(state, data) {
    state.paymentData.paywayGroups = data
  },
  SET_PAYMENT_ERROR_INVALID_FIELDS(state, data) {
    state.error.invalidFields = data
  },
  SET_SESSION_TIMER_ACTIVE(state, value) {
    state.session.active = value
  },
  SESSION_REFRESH_IN_PROGRESS(state, data) {
    state.session.refreshing = data
  },
  SET_CURRENT_TIMESTAMP(state, data) {
    state.session.currentTimestamp = data
  },
  SET_SELECT_OTHER(state, data) {
    state.selectOther = data
  },
  SET_PAYWAY_GROUP_TYPE(state, value) {
    state.selectedPaywayGroupType = value
  },
  SET_PAYWAY_GROUP_ID(state, value) {
    state.selectedPaywayGroupId = value
  },
  SET_WALLET_ORDER_VARIANT(state, value) {
    state.walletOrderVariant = value
  },
  SET_PAYMENT_PAYWALL_VIEW(state, value) {
    state.paywallView = value
  },
  SET_PAYMENT_PAYWALL_MODE(state, value) {
    state.paywallMode = value
  },
  SET_PAYWAY_METHOD(state, value) {
    state.paywayMethod = value
  },
  SET_PAYWALL_THEME(state, value) {
    state.paywallTheme = value
  },
  SET_RATE_LIMIT_EXCEEDED(state, value) {
    state.rateLimitExceeded = value
  },
  SET_PAYMENT_RELOAD_ACTIVE_PAYWAY(state, value) {
    state.reloadActivePayway = value
  },
  SET_PAYMENT_BOOST_ACTIVE(state, value) {
    state.isPaymentBoostActive = value
  },
  SET_PAYMENT_BOOST_GROUPS_HIDDEN(state, value) {
    state.isPaymentBoostGroupsHidden = value
  },
  SET_PAYMENT_LAST_USED_PAYWAY(state, value) {
    state.paymentData.lastUsedPayway = value
  },
  SET_PAYER_PAYWALL_HISTORY(state, value) {
    state.payerPaywallHistory = value
  },
  SET_PAYER_PAYWALL_RETURN(state, value) {
    state.payerPaywallReturn = value
  },
  SET_PAYER_PAYWALL_COMPLETED(state, value) {
    state.payerPaywallCompleted = value
  },
  // Odpowiedź /payer-paywall/update zwraca zaktualizowane dane transakcji
  // (kwota + koszyk po doliczeniu opłaty za usługę) - nadpisujemy je w paymentData,
  // bo widoki (Basket, OrderDetails, paywall) czytają kwotę i koszyk wprost ze store'a.
  SET_PAYER_PAYWALL_TRANSACTION_UPDATE(state, data) {
    if (!state.paymentData || !data) {
      return
    }
    const transaction = Object.assign({}, state.paymentData.transaction || {})
    if (data.transactionAmount !== undefined && data.transactionAmount !== null) {
      state.paymentData.payAmount = data.transactionAmount
      state.paymentData.startAmount = data.transactionAmount
      transaction.payAmount = data.transactionAmount
    }
    if (data.currency) {
      state.paymentData.currency = data.currency
      state.paymentData.payAmountCurrency = data.currency
      transaction.payAmountCurrency = data.currency
    }
    if (data.productBasket) {
      state.paymentData.productBasket = data.productBasket
    }
    state.paymentData.transaction = transaction
  },
}

const actions = {
  getPaywaysLanguageData({ commit, getters, state }) {
    if (!state.paymentData || !state.paymentData.paywayGroups) {
      return Promise.resolve()
    }
    const payways = []

    state.paymentData.paywayGroups.forEach((group) => {
      group.payways.forEach((payway) => {
        payways.push(payway.id)
      })
    })

    const params = [
      payways.map((paywayId) => `paywayIds=${paywayId}`).join('&'),
      `serviceId=${state.paymentData.serviceID}`,
      `language=${getters.getCurrentLanguage}`,
    ]
    const url = ENDPOINTS.languageData + `?${params.join('&')}`
    return apiClass
      .get(url)
      .then((response) => {
        commit('SET_PAYWAYS_LANGUAGE_DATA', response.body.paywayLanguageData)
      })
      .catch((error) => {
        if (ToolsClass.isDebugEnabled()) {
          console.log('error', error)
        }
      })
  },
  setPaymentLanguage({ dispatch, getters }, data) {
    const queryParams = ToolsClass.getQueryParams()
    const languagesList = getters.getAvailableLanguagesList
    const paymentLang = data.languageData?.languageChosen
    const cookieLang = CookieClass.getCookie(COOKIE_LANG)?.toUpperCase()
    const browserLang = navigator.language.substring(0, 2).toUpperCase()
    const queryLang = queryParams.lang?.toUpperCase()

    // 1. Jeśli mamy język z URLa oraz dostępną listę języków - zawsze ustawiamy język z URLa
    if (Array.isArray(languagesList) && queryLang && languagesList.includes(queryLang)) {
      return dispatch('setLanguage', { value: queryLang, silent: true })
    }

    // 2. Jeśli mamy język z transakcji oraz dostępną listę języków - zawsze ustawiamy język z transakcji
    if (Array.isArray(languagesList) && paymentLang && languagesList.includes(paymentLang)) {
      return dispatch('setLanguage', { value: paymentLang, silent: true })
    }

    // 3. Jeśli nie mamy listy i/lub języka z transakcji, sprawdzamy cookie
    if (cookieLang && LANGUAGES_ACTIVE_LIST.includes(cookieLang)) {
      return dispatch('setLanguage', { value: cookieLang, silent: true })
    }

    // 4. Jeśli nie mamy info z cookie, sprawdzamy język przeglądarki
    if (browserLang && LANGUAGES_ACTIVE_LIST.includes(browserLang)) {
      return dispatch('setLanguage', { value: browserLang, silent: true })
    }

    // 5. ostatecznie ustawiamy domyślny język
    return dispatch('setLanguage', { value: LANG_DEFAULT, silent: true })
  },
  getPaymentStartTrx({ commit, state, dispatch }, { apiToken }) {
    commit('SET_RATE_LIMIT_EXCEEDED', false)
    commit('SET_PAYMENT_LOADING', true)
    commit('SET_PAYMENT_API_TOKEN', apiToken)
    commit('SET_PAYMENT_ERROR_INVALID_FIELDS', null)

    const headers = {
      'Referer-Paywall': document.referrer,
    }
    const data = {
      apiToken,
      contextName: state.contextName,
      // Sekcja `browser` idzie też na /start i /continue (PEP-1432). → `utils/browserData.js`
      browser: getBrowserData(),
    }

    return apiClass
      .post(ENDPOINTS.start, data, { headers })
      .then((response) => {
        return processPaymentData(commit, dispatch, response.body.data).then((data) => {
          if (data.isPayerPaywall) {
            commit('SET_PAYER_PAYWALL_RETURN', { name: 'Start', params: { apiToken } })
          }
          return setPaywall(commit, data)
        })
      })
      .catch((error) => {
        if (error.status === HTTP_STATUS.RATE_LIMIT_EXCEEDED) {
          commit('SET_RATE_LIMIT_EXCEEDED', true)
          return Promise.reject(error)
        }

        if (error === 'psd2Data') {
          return Promise.reject(error)
        }

        if (error.body) {
          dispatch('setPaymentLanguage', error.body.data)
        }
        commit('SET_PAYMENT_LOADING', false)
        commit('SET_PAYMENT_PAYWALL_VIEW', false)
        if (error.body && error.body.invalidFields) {
          commit('SET_PAYMENT_ERROR_INVALID_FIELDS', error.body.invalidFields)
        }
        return Promise.reject(error)
      })
  },
  getPaymentVisadataTrx({ commit, state, dispatch }, { apiToken, idTransRemote }) {
    commit('SET_PAYMENT_LOADING', true)
    commit('SET_PAYMENT_API_TOKEN', apiToken)
    commit('SET_PAYMENT_TRANS_REMOTE_ID', idTransRemote)
    commit('SET_PAYMENT_ERROR_INVALID_FIELDS', null)

    const headers = {
      'Referer-Paywall': document.referrer,
    }
    const data = {
      apiToken,
      idTransRemote,
      contextName: state.contextName,
    }

    return apiClass
      .post(ENDPOINTS.visadata, data, { headers })
      .then((response) => {
        return processPaymentData(commit, dispatch, response.body.data).then(() => {
          commit('SET_PAYMENT_LOADING', false)
          return Promise.resolve()
        })
      })
      .catch((error) => {
        commit('SET_PAYMENT_LOADING', false)
        commit('SET_PAYMENT_PAYWALL_VIEW', false)
        if (error.body && error.body.invalidFields) {
          commit('SET_PAYMENT_ERROR_INVALID_FIELDS', error.body.invalidFields)
        }
        return Promise.reject(error)
      })
  },
  getPaymentRegulationsApprovalStart({ commit, state, dispatch }, { apiToken }) {
    commit('SET_PAYMENT_LOADING', true)
    commit('SET_PAYMENT_API_TOKEN', apiToken)
    commit('SET_PAYMENT_ERROR_INVALID_FIELDS', null)

    const data = {
      apiToken,
      contextName: state.contextName,
    }

    return apiClass
      .post(ENDPOINTS.regulationsApprovalStart, data)
      .then((response) => {
        return processPaymentData(commit, dispatch, response.body.startResponseData).then((data) => {
          dispatch('setPaymentActivePayway', data.chosenPayway)
          commit('SET_PAYMENT_PAYWALL_MODE', PAYWALL_MODE.regulationsApproval)
          commit('SET_PAYMENT_LOADING', false)
          return Promise.resolve()
        })
      })
      .catch((error) => {
        commit('SET_PAYMENT_LOADING', false)
        if (error.body && error.body.invalidFields) {
          commit('SET_PAYMENT_ERROR_INVALID_FIELDS', error.body.invalidFields)
        }
        return Promise.reject(error)
      })
  },
  getPaymentContinueTrx({ commit, state, dispatch, getters }, { idTransRemote, continueHash, sid }) {
    commit('SET_PAYMENT_LOADING', true)
    commit('SET_PAYMENT_ERROR_INVALID_FIELDS', null)
    commit('SET_PAYMENT_TRANS_REMOTE_ID', idTransRemote)
    commit('SET_PAYMENT_CONTINUE_HASH', continueHash)

    const headers = {
      'Referer-Paywall': document.referrer,
    }
    const data = {
      idTransRemote,
      continueHash,
      contextName: state.contextName,
      // Sekcja `browser` idzie też na /start i /continue (PEP-1432). → `utils/browserData.js`
      browser: getBrowserData(),
    }

    if (sid) {
      data.sid = sid
    }

    if (ToolsClass.isVersionSDKSet()) {
      data.versionSDK = ToolsClass.getVersionSDK()
    }
    if (ToolsClass.isScreenTypeSet()) {
      data.screenType = ToolsClass.getScreenType()
    }

    return apiClass
      .post(ENDPOINTS.continue, data, { headers })
      .then((response) => {
        commit('SET_PAYMENT_API_TOKEN', response.body.data.apiToken)
        return processPaymentData(commit, dispatch, response.body.data).then((data) => {
          const paywayList = getters.getPaywayList
          if (paywayList) {
            const paywayObject = paywayList.find((payway) => payway.id === data.chosenPaywayId)
            if (paywayObject) {
              dispatch('setPaymentActivePayway', paywayObject)
            }
          }
          if (data.isPayerPaywall) {
            commit('SET_PAYER_PAYWALL_RETURN', { name: 'Continue', params: { idTransRemote, continueHash } })
          }
          return setPaywall(commit, data)
        })
      })
      .catch((error) => {
        if (error.status === HTTP_STATUS.RATE_LIMIT_EXCEEDED) {
          commit('SET_RATE_LIMIT_EXCEEDED', true)
          return Promise.reject(error)
        }

        if (error.message === 'psd2Data') {
          return Promise.reject(error)
        }

        if (error.body && error.body?.data) {
          dispatch('setPaymentLanguage', error.body.data)
          commit('SET_PAYMENT_LOADING', false)
        }

        if (error.body && error.body.invalidFields) {
          commit('SET_PAYMENT_ERROR_INVALID_FIELDS', error.body.invalidFields)
        }
        return Promise.reject(error)
      })
  },
  getPaymentRegulationsApprovalContinueTrx({ commit, state, dispatch, getters }, { idTransRemote, continueHash }) {
    commit('SET_PAYMENT_LOADING', true)
    commit('SET_PAYMENT_ERROR_INVALID_FIELDS', null)
    commit('SET_PAYMENT_TRANS_REMOTE_ID', idTransRemote)
    commit('SET_PAYMENT_CONTINUE_HASH', continueHash)

    const headers = {
      'Referer-Paywall': document.referrer,
    }
    const data = {
      idTransRemote,
      continueHash,
      contextName: state.contextName,
      // Sekcja `browser` idzie też na /start i /continue (PEP-1432). → `utils/browserData.js`
      browser: getBrowserData(),
    }

    if (ToolsClass.isVersionSDKSet()) {
      data.versionSDK = ToolsClass.getVersionSDK()
    }
    if (ToolsClass.isScreenTypeSet()) {
      data.screenType = ToolsClass.getScreenType()
    }

    return apiClass
      .post(ENDPOINTS.continue, data, { headers })
      .then((response) => {
        return processPaymentData(commit, dispatch, response.body.data).then((data) => {
          const paywayList = getters.getPaywayList
          if (paywayList) {
            const paywayObject = paywayList.find((payway) => payway.id === data.chosenPaywayId)
            if (paywayObject) {
              dispatch('setPaymentActivePayway', paywayObject)
            }
          }
          commit('SET_PAYMENT_API_TOKEN', data.apiToken)
          commit('SET_PAYMENT_PAYWALL_MODE', PAYWALL_MODE.regulationsApproval)
          return Promise.resolve()
        })
      })
      .catch((error) => {
        commit('SET_PAYMENT_LOADING', false)
        if (error.body && error.body.invalidFields) {
          commit('SET_PAYMENT_ERROR_INVALID_FIELDS', error.body.invalidFields)
        }
        return Promise.reject(error)
      })
  },
  getPaymentWaitData({ commit, state, dispatch }, { apiToken, idTransRemote }) {
    commit('SET_PAYMENT_LOADING', true)
    commit('SET_PAYMENT_API_TOKEN', apiToken)
    commit('SET_PAYMENT_TRANS_REMOTE_ID', idTransRemote)
    commit('SET_PAYMENT_ERROR_INVALID_FIELDS', null)

    const data = {
      apiToken,
      idTransRemote,
    }

    return apiClass
      .post(ENDPOINTS.wait.getData, data)
      .then((response) => {
        const data = response.body.data
        commit('SET_PAYMENT_PAYWALL_VIEW', false)
        commit('SET_PAYMENT_DATA', data)

        // jeśli start z WL z kanałem PISowym i brak danych psd2 -> przekierowanie na potykacz PISowy
        if ([PAYMENT_CLIENT_SOURCE.psd2Data].includes(data.clientSource)) {
          commit('SET_PAYMENT_PAYWALL_VIEW', false)
          if (!data.psd2Data) {
            return Promise.reject(new Error('psd2Data'))
          }
        }

        return Promise.resolve()
      })
      .catch((error) => {
        commit('SET_PAYMENT_LOADING', false)
        commit('SET_PAYMENT_PAYWALL_VIEW', false)
        if (error.body && error.body.invalidFields) {
          commit('SET_PAYMENT_ERROR_INVALID_FIELDS', error.body.invalidFields)
        }
        return Promise.reject(error)
      })
  },
  sendPaymentUpdateTrx({ commit, state, getters }, { paymentToken, marketing, requiredParams }) {
    commit('SET_PAYMENT_PROCESSING', true)
    commit('SET_PAYMENT_ERROR_INVALID_FIELDS', null)
    commit('SET_PAYMENT_PAYWALL_VIEW', false)

    const data = Object.assign({}, state.form)
    data.apiToken = state.apiToken
    data.marketingAgreements = []
    data.requiredParams = requiredParams
    const authorizationCode = extractBlikDigits(state.form.blikCode)
    if (authorizationCode) {
      data.authorizationCode = authorizationCode
    }
    if (paymentToken) {
      data.paymentToken = paymentToken
    }
    if (marketing) {
      const marketingList = getters.getMarketingAgreementList
      Object.keys(marketing).forEach((key) => {
        if (marketing[key]) {
          const agreement = marketingList.find((item) => item.code === key)
          if (agreement) {
            data.marketingAgreements.push({
              code: key,
              language: agreement.language,
              agree: true,
            })
          }
        }
      })
    }
    delete data.blikCode

    // Sekcja `browser` idzie dla wszystkich (PEP-1433)
    data.browser = getBrowserData()

    const orderedPaywayID = data.paywayID

    return apiClass
      .post(ENDPOINTS.updatePayway, data)
      .then((response) => {
        // Backend przyjął zlecenie — jedno miejsce dla wszystkich kanałów, także portfeli (PEP-1380)
        trackStep(TRX_STEP.paymentOrdered, {
          name: orderedPaywayID,
          payway: orderedPaywayID,
          group: MATOMO_PAYMENT_TYPE_GROUP[state.selectedPaywayGroupType] || state.selectedPaywayGroupType,
        })
        const data = setDataTransaction(response.body.data)
        commit('SET_PAYMENT_DATA', data)
        if ([PAYMENT_CLIENT_SOURCE.frData, PAYMENT_CLIENT_SOURCE.visaMobileData].includes(data.clientSource)) {
          commit('SET_PAYMENT_PROCESSING', false)
        }
        return Promise.resolve()
      })
      .catch((error) => {
        commit('SET_PAYMENT_PROCESSING', false)
        if (error.body && error.body.invalidFields) {
          commit('SET_PAYMENT_ERROR_INVALID_FIELDS', error.body.invalidFields)
        }
        return Promise.reject(error)
      })
  },
  sendPaymentRejectTrx({ commit, state }) {
    // Powrót do sklepu z aktywnego paywalla = porzucenie transakcji.
    // Strażnik state.paywallView (jeszcze true) wyklucza payer_paywall i fallbacki bez widoku paywalla.
    if (state.paywallView) {
      MatomoClass.sendAbandonTransactionEvent(
        state.paymentData.serviceID,
        state.apiToken,
        state.paymentData.transaction?.orderId
      )
    }
    commit('SET_PAYMENT_LOADING', true)
    commit('SET_PAYMENT_PAYWALL_VIEW', false)
    const data = {
      merchantId: 0,
      serviceId: state.paymentData.serviceID,
      idTransRemote: state.paymentData.transRemoteID,
    }
    return apiClass
      .post(ENDPOINTS.reject, data)
      .then((response) => {
        if (response.body.rejectResponseData && response.body.rejectResponseData.returnUrl) {
          return Promise.resolve(response.body.rejectResponseData.returnUrl)
        }
        return Promise.reject(response)
      })
      .catch((error) => {
        commit('SET_PAYMENT_LOADING', false)
        return Promise.reject(error)
      })
  },
  setPaymentGroupSelected({ commit, state }, group) {
    commit('SET_PAYWAY_GROUP_TYPE', group.type)
    commit('SET_PAYWAY_GROUP_ID', group.id)
    return Promise.resolve()
  },
  /**
   * Startuje sesję merchanta Apple Pay na bramce kartowej (`postCards`, host `VITE_APP_CARDS_API`),
   * nie na `trx-api`. Zwraca **obiekt** sesji gotowy dla `completeMerchantValidation` - backend oddaje
   * go jako łańcuch JSON w `data.session`, a Apple przyjmuje wyłącznie obiekt.
   *
   * `idTransRemote` to identyfikator transakcji Autopay (`transRemoteID`), tak samo jak w pozostałych
   * wywołaniach tej bramki - NIE `transaction.orderId` (numer zamówienia sklepu), który szedł do
   * poprzedniego endpointu na `trx-api`.
   *
   * Odpowiedzi błędne (422 `INVALID`, 400 `PROCESSING_ERROR` / `CONFIGURATION_MISSING`) mają status
   * HTTP poza 2xx, więc odrzuca je już `http-compat`; `errorCode` zostaje w `error.body`.
   *
   * @param {object} context
   * @param {string} url wartość `event.validationURL` z `onvalidatemerchant`
   * @returns {Promise<object>} obiekt sesji merchanta
   */
  getApplePaySession({ state }, url) {
    const data = {
      idTransRemote: state.paymentData.transRemoteID,
      validationUrl: url,
    }

    return apiClass
      .postCards(ENDPOINTS.cards.applePay.session, data)
      .then((response) => {
        if (response.body?.status !== 'SUCCESS' || !response.body?.data?.session) {
          return Promise.reject(new Error(`ApplePay session: ${response.body?.errorCode ?? response.body?.status}`))
        }
        try {
          return Promise.resolve(JSON.parse(response.body.data.session))
        } catch (error) {
          return Promise.reject(error)
        }
      })
      .catch((error) => {
        return Promise.reject(error)
      })
  },
  getWalletData({ state }) {
    const data = {
      serviceId: state.paymentData.serviceID,
      serviceUrl: state.paymentData.serviceUrl,
    }

    return apiClass
      .post(ENDPOINTS.walletData, data)
      .then((response) => {
        return Promise.resolve(response.body)
      })
      .catch((error) => {
        return Promise.reject(error)
      })
  },
  getSessionRemainingTime({ commit, state }) {
    commit('SESSION_REFRESH_IN_PROGRESS', true)
    const data = {
      apiToken: state.apiToken,
      idTransRemote: state.transRemoteID,
    }
    return apiClass
      .post(ENDPOINTS.session.getRemainingTime, data)
      .then((response) => {
        return Promise.resolve(response.body.sessionRemainingTime)
      })
      .catch((error) => {
        return Promise.reject(error)
      })
      .finally(() => {
        commit('SESSION_REFRESH_IN_PROGRESS', false)
      })
  },
  disableSessionTimer({ commit }) {
    commit('SET_SESSION_TIMER_ACTIVE', false)
  },
  refreshSession({ commit, state }) {
    commit('SESSION_REFRESH_IN_PROGRESS', true)
    const data = {
      apiToken: state.apiToken,
      idTransRemote: state.paymentData.transRemoteID,
    }
    return apiClass
      .post(ENDPOINTS.session.refresh, data)
      .then((response) => {
        return Promise.resolve(response.body.sessionRemainingTime)
      })
      .catch((error) => {
        return Promise.reject(error)
      })
      .finally(() => {
        commit('SESSION_REFRESH_IN_PROGRESS', false)
      })
  },
  rejectSession({ state }) {
    const data = {
      apiToken: state.apiToken,
      idTransRemote: state.paymentData.transRemoteID,
    }
    return apiClass
      .post(ENDPOINTS.session.reject, data)
      .then(() => {
        return Promise.resolve()
      })
      .catch((error) => {
        return Promise.reject(error)
      })
  },
  setPaymentActivePayway({ commit, state, getters }, payway) {
    commit('SET_PAYMENT_FORM_REGULATION', null)
    commit('SET_PAYMENT_FORM_REGULATION_ID', null)
    commit('SET_PAYMENT_FORM_REGULATION_APPROVED', false)
    commit('SET_PAYMENT_FORM_RECURRING_ACCEPTANCE_STATE_AFTER', null)
    commit('SET_PAYMENT_FORM_BLIK_CODE', null)

    if (!payway) {
      commit('SET_SELECT_OTHER', true)
      commit('SET_PAYMENT_FORM_PAYWAY_ID', null)
      syncC2PTransactionAmount(commit, state, null)
      return Promise.resolve()
    }

    commit('SET_SELECT_OTHER', false)
    commit('SET_PAYMENT_FORM_PAYWAY_ID', payway.id)
    syncC2PTransactionAmount(commit, state, payway.id)

    const regulation = findRegulationForPayway(state, payway, getters.getCurrentLanguage)
    if (regulation) {
      const regulationType = payway.recurring ? REGULATION_TYPE.recurring : REGULATION_TYPE.main
      commit('SET_PAYMENT_FORM_REGULATION_ID', regulation.regulationId)
      commit('SET_PAYMENT_FORM_REGULATION', regulation)
      // jeśli regulamin jest ukryty, oznaczamy go jako zaakceptowany
      if (regulation.checkedAndHidden) {
        commit('SET_PAYMENT_FORM_REGULATION_APPROVED', true)
      }
      if (regulationType === REGULATION_TYPE.recurring) {
        commit('SET_PAYMENT_FORM_RECURRING_ACCEPTANCE_STATE_AFTER', 'ACCEPTED')
      }
    }
    return Promise.resolve()
  },
  /**
   * Przestawia regulamin wybranego kanału na wariant w BIEŻĄCYM języku.
   *
   * Wołane z `setLanguage` - inaczej treść zostaje w języku z chwili wyboru kanału (patrz komentarz
   * przy `findRegulationForPayway`). NIE rusza `regulationApproved`: użytkownik zgadza się na ten sam
   * dokument, tylko czyta go w innym języku, a zdjęcie zgody skasowałoby mu zaznaczony checkbox.
   */
  refreshActiveRegulationLanguage({ commit, state, getters }) {
    if (!state.form.paywayID || !state.form.regulation) {
      return Promise.resolve()
    }
    const payway = getters.getPaywayList.find((item) => item.id === state.form.paywayID)
    if (!payway) {
      return Promise.resolve()
    }
    const regulation = findRegulationForPayway(state, payway, getters.getCurrentLanguage)
    // brak wariantu w nowym języku - zostawiamy dotychczasowy, lepszy niż puste oświadczenie
    if (!regulation || regulation.regulationId === state.form.regulationAcceptanceId) {
      return Promise.resolve()
    }
    commit('SET_PAYMENT_FORM_REGULATION_ID', regulation.regulationId)
    commit('SET_PAYMENT_FORM_REGULATION', regulation)
    return Promise.resolve()
  },
  cancelVisaMobileTransaction({ state }) {
    const data = {
      idTransRemote: state.paymentData.transRemoteID,
    }
    return apiClass
      .postCards(ENDPOINTS.cards.visaMobile.cancel, data)
      .then((response) => {
        if (response.body.status !== 'SUCCESS') {
          return Promise.reject(response.body.status)
        }
        return Promise.resolve()
      })
      .catch((error) => {
        return Promise.reject(error)
      })
  },
  changePaywallMode({ commit }, mode) {
    commit('SET_PAYMENT_PAYWALL_MODE', mode)
    return Promise.resolve()
  },
  // Odsłania paywall/realizację na podstawie danych już wczytanych w store (reuse - bez
  // ponownego zapytania do API). Zwraca promise z setPaywall - dla kanału PISowego bez danych
  // psd2 odrzuca 'psd2Data', dzięki czemu widok (Start/Continue) obsłuży redirect na potykacz,
  // tak samo jak przy pierwszym wejściu.
  revealStoredPaywall({ commit, state }) {
    // Źródła obsługiwane przez loader (gotowy formularz, `psd2Data`) wysyła `Payment/Loader/Loading.vue`,
    // który renderuje się tylko pod `loading`. Przy zwykłym wejściu `setPaywall` go nie zdejmuje, ale
    // bramka payer-paywalla już to zrobiła - bez ponownego podniesienia widok po powrocie z formularza
    // firmowego nie ma czego pokazać i zostaje pusty (PEP-1339).
    if (LOADER_CLIENT_SOURCE_LIST.includes(state.paymentData?.clientSource)) {
      commit('SET_PAYMENT_LOADING', true)
    }
    // ta sama logika co przy wejściu, ale z deaktywowaną bramką isPayerPaywall
    return setPaywall(commit, Object.assign({}, state.paymentData, { isPayerPaywall: false }))
  },
}

const getters = {
  getMarketingAgreementList(state, getters) {
    const langLower = getters.getCurrentLanguage.toLowerCase()
    const langUpper = getters.getCurrentLanguage.toUpperCase()
    return (
      state.paymentData?.marketingAgreements?.filter(
        (agreement) => agreement.language === langLower || agreement.language === langUpper
      ) || []
    )
  },
  getPaywayList(state) {
    return generatePaywayList(state)
  },
  getPaymentGroupList(state, getters) {
    if (state.loading) {
      return []
    }
    return generatePaymentGroupsForGL30Response(state, getters)
  },
  isPaymentCardAvailable(state, getters) {
    const paywayList = getters.getPaywayList
    let result = false
    paywayList.forEach((payway) => {
      if (CARD_ID_LIST.includes(payway.id)) {
        result = true
      }
    })
    return result
  },
  isPayerPaywallRequired(state) {
    return !!state.paymentData?.isPayerPaywall && !state.payerPaywallCompleted
  },
}

export default {
  state,
  mutations,
  actions,
  getters,
}

/**
 * @param commit
 * @param dispatch
 * @param {object} data
 * @returns {*}
 */
const processPaymentData = (commit, dispatch, data) => {
  data = setDataTransaction(data)
  const customerEmail = data.additionalData.customerEmail
  if (customerEmail) {
    commit('SET_PAYMENT_FORM_EMAIL', customerEmail)
  }

  // jesli mamy aktywny mechanizm payment boost, sprawdzamy lastUsedPayway
  if (PaymentBoost.isPaymentBoostActive(data.serviceID, data.transaction.payAmount)) {
    if (
      data.lastUsedPayway &&
      [
        PAYWAY_GROUPS_TYPES.card,
        PAYWAY_GROUPS_TYPES.visaMobile,
        PAYWAY_GROUPS_TYPES.wallet,
        PAYWAY_GROUPS_TYPES.googlePay,
        PAYWAY_GROUPS_TYPES.applePay,
      ].includes(data.lastUsedPayway.type)
    ) {
      data.lastUsedPayway = null
    }
    commit('SET_PAYMENT_BOOST_ACTIVE', true)
    commit('SET_PAYMENT_BOOST_GROUPS_HIDDEN', true)
  }

  // jeśli grupy po nowemu z API
  if (data.paywayGroups) {
    data.paywayGroups.forEach((group) => {
      group.id = ToolsClass.generateUUID()
    })
  }

  commit('SET_PAYMENT_DATA', data)
  commit('SET_PAYMENT_FORM_SERVICE_ID', data.serviceID)
  commit('SET_PAYMENT_TRANS_REMOTE_ID', data.transRemoteID)
  commit('SET_PAYMENT_FORM_TRANS_REMOTE_ID', data.transRemoteID)
  // Jedno miejsce dla start, continue, visadata, regulations-approval i payer-paywall (PEP-1380)
  identifyTransaction(data.transRemoteID, { serviceId: data.serviceID, orderId: data.additionalData?.orderId })

  // jeśli mamy prowizje i mamy różne stawki prowizyjne, a ostatnim kanałem jest BLIK - zapominamy go
  if (
    isPaymentFeeActive({ paymentData: data }) &&
    isPaymentFeeDifferent({ paymentData: data }) &&
    PAYMENT_FEE_SID_CONFIG.includes(Number(data.serviceID))
  ) {
    if (data.lastUsedPayway && [PAYWAY_GROUPS_TYPES.blik].includes(data.lastUsedPayway.type)) {
      commit('SET_PAYMENT_LAST_USED_PAYWAY', null)
    }
  }

  return dispatch('setPaymentLanguage', data).then(() => {
    return Promise.resolve(data)
  })
}

/**
 * @param {object} data
 * @returns {*}
 */
const setDataTransaction = (data) => {
  data.transaction = {
    orderId: data.additionalData.orderId,
    // Tryb legacy (`customerFee` na korzeniu) ma prowizję już w `payAmount` - patrz komentarz przy
    // `getTransactionBaseAmount` (SD-42480).
    payAmount: getTransactionBaseAmount(data),
    payAmountCurrency: data.payAmountCurrency,
    receiverNameForFront: data.receiverNameForFront,
    customerNumber: data.additionalData.customerNumber ? data.additionalData.customerNumber : null,
    invoiceNumber: data.additionalData.invoiceNumber ? data.additionalData.invoiceNumber : null,
    accountNumber: data.additionalData.accountNumber ? data.additionalData.accountNumber : null,
  }
  return data
}

/**
 * Synchronizacja kwoty transakcji trzymanej w module `c2p` z aktualnie wybranym kanałem.
 *
 * Ta kwota jest ustawiana raz przy inicjalizacji Click to Pay (`setC2PInitData`, wołane
 * w `Paywall.onMounted`) i idzie potem do `getC2PCheckout` jako `dpaTransactionOptions`,
 * czyli obciąża konsumenta. Prowizja płatnika zależy jednak od WYBRANEGO kanału, a ten
 * może się zmienić dowolną liczbę razy po inicjalizacji - bez tej synchronizacji checkout
 * leciałby na kwotę z chwili mountu paywalla.
 *
 * Walutę przywracamy razem z kwotą, bo obie nadpisuje przewalutowanie (`changeTransactionDataByDcc`
 * w `Paywall/Form.vue`). Zmiana kanału i tak unieważnia DCC (widget kart jest resetowany, kurs
 * zostanie policzony od nowa), więc zostawienie waluty DCC przy kwocie bazowej byłoby rozjazdem.
 *
 * @param {function} commit
 * @param {object} state
 * @param {number|string|null} paywayID
 * @returns {void}
 */
const syncC2PTransactionAmount = (commit, state, paywayID) => {
  // Strażnik patrzy na `transaction`, nie na samo `paymentData`: akcja jest wołana także z widoków
  // bez danych transakcji (`RegulationsApproval`), gdzie kwota wyszłaby zerowa, a waluta pusta -
  // a Click to Pay i tak tam nie działa, więc nie ma czego synchronizować.
  if (!state.paymentData?.transaction) {
    return
  }
  commit('SET_C2P_TRANSACTION_AMOUNT', getTransactionAmount({ paymentData: state.paymentData, paywayID }))
  commit('SET_C2P_TRANSACTION_CURRENCY', state.paymentData.transaction.payAmountCurrency ?? null)
}

/**
 * Zlecenie płatności przy starcie z już wybranym kanałem (PEP-1380). Odpowiedź `/start` albo
 * `/continue` niesie wtedy od razu formularz do banku (albo `cardsData`), paywalla nie ma i front nie
 * woła `/update-payway` — jedynego miejsca, które poza tym wysyła `platnosc_zlecona`. Bez tego taka
 * transakcja ma w Matomo tylko `widok Start` i `wynik`.
 *
 * Wołane z `setPaywall` ZA bramką payer-paywalla: formularz wychodzi dopiero po
 * `revealStoredPaywall`. `chosenPaywayId` backend wysyła wyłącznie przy kanale wybranym przed bramką,
 * więc jest jednocześnie warunkiem i kanałem. Grupy odpowiedź nie niesie — zostaje pusta.
 *
 * @param {object} data dane transakcji z odpowiedzi startu
 */
const trackPreselectedPaymentOrdered = (data) => {
  if (!data.chosenPaywayId) {
    return
  }
  const isFormReady = LOADER_CLIENT_SOURCE_LIST.includes(data.clientSource)
  const isCardsFlow = data.clientSource === PAYMENT_CLIENT_SOURCE.cardsData
  if (!isFormReady && !isCardsFlow) {
    return
  }
  trackStep(TRX_STEP.paymentOrdered, { name: data.chosenPaywayId, payway: data.chosenPaywayId })
}

/**
 * @param commit
 * @param {object} data
 * @returns {Promise<never>|Promise<void>}
 */
const setPaywall = (commit, data) => {
  // GATE payer-paywall: nie odsłaniamy paywalla, czekamy na formularz danych firmowych.
  // Widok wykryje getters.isPayerPaywallRequired i przekieruje na /payer-paywall.
  if (data.isPayerPaywall) {
    commit('SET_PAYMENT_LOADING', false)
    commit('SET_PAYMENT_PAYWALL_VIEW', false)
    return Promise.resolve()
  }
  // jeśli start z WL z kanałem PISowym i brak danych psd2 -> przekierowanie na potykacz PISowy
  if ([PAYMENT_CLIENT_SOURCE.psd2Data].includes(data.clientSource)) {
    commit('SET_PAYMENT_PAYWALL_VIEW', false)
    if (!data.psd2Data) {
      commit('SET_PAYMENT_LOADING', false)
      return Promise.reject(new Error('psd2Data'))
    }
  }
  trackPreselectedPaymentOrdered(data)
  // Wejście z transakcji wystartowanej w White-Label z kanałem kartowym w nowym trybie (PEP-799):
  // `/continue` (albo `/start`) oddaje od razu `cardsData`, bez etapu paywalla. Przepływem zajmuje
  // się dalej `Processing.vue` → `/card-status`, więc tutaj wystarczy postawić widok realizacji.
  //
  // ⚠️ `SET_PAYMENT_PROCESSING` jest tu KONIECZNE, nie kosmetyczne: bez niego `loading` zostaje
  // podniesiony, widok renderuje `PaymentLoading` (który tego źródła nie obsługuje) i użytkownik
  // patrzy na kręciołek do końca sesji. Ta sama pułapka co przy `psd2Data`, tylko odwrotna:
  // tam loader JEST widokiem docelowym, bo montuje `PaymentPsd2Data`.
  if ([PAYMENT_CLIENT_SOURCE.cardsData].includes(data.clientSource)) {
    commit('SET_PAYMENT_LOADING', false)
    commit('SET_PAYMENT_PAYWALL_VIEW', false)
    commit('SET_PAYMENT_PROCESSING', true)
    return Promise.resolve()
  }
  if ([PAYMENT_CLIENT_SOURCE.frData, PAYMENT_CLIENT_SOURCE.payways].includes(data.clientSource)) {
    if (data.chosenPaywayId) {
      commit('SET_PAYMENT_PAYWALL_MODE', PAYWALL_MODE.chosenPayway)
    }
    commit('SET_PAYMENT_LOADING', false)
    commit('SET_PAYMENT_PAYWALL_VIEW', true)
  }
  if ([PAYMENT_CLIENT_SOURCE.frData].includes(data.clientSource)) {
    commit('SET_PAYMENT_FORM_PAYWAY_ID', data.chosenPaywayId)
    commit('SET_PAYMENT_PAYWALL_VIEW', false)
  }
  return Promise.resolve()
}

/**
 * Metoda czyszcząca listę grup z grup pustych, bez kanałów płatności
 * @param {array} paywayGroups
 * @returns {*}
 */
const getNotEmptyPaywayGroups = (paywayGroups) => {
  const result = []
  paywayGroups.forEach((group) => {
    if (group.payways.length) {
      group.active = group.payways.reduce((acc, payway) => {
        return acc || payway.active
      }, false)
      result.push(group)
    }
  })
  return ToolsClass.sortArrayByObjectProperty(result, 'order')
}

/**
 * Metoda generująca grupę promocyjną
 * @param getters
 * @param apiFrontType
 * @param group
 * @param state
 * @returns {*}
 */
const generatePromoGroup = (getters, apiFrontType, group, state) => {
  const newGroup = ToolsClass.deepExtend(PAYWAY_GROUP_MODEL)
  newGroup.type = apiFrontType
  // Pierwszeństwo ma `/language-data` - reaguje na zmianę języka. Nagłówek z samej grupy (wsad) to
  // zapas w języku transakcji: grupa PROMO nie ma etykiety z i18n, więc bez niego nieudane
  // `/language-data` (np. 500, połykane w `getPaywaysLanguageData`) zostawiało kafel bez nazwy.
  // → doc/dokumentacja/platnosci.md („Grupa promocyjna — etykieta")
  const promoLanguageData =
    state.paywaysLanguageData?.[group.payways[0]?.id]?.promotionLanguageData ?? group.promotionLanguageData
  if (promoLanguageData) {
    newGroup.label = promoLanguageData.header
    newGroup.description = promoLanguageData.tooltip
  }
  newGroup.isNew = group.promoType === PROMOTION_TYPES.novelty
  newGroup.isMastercard = group.promoType === PROMOTION_TYPES.mastercard
  newGroup.isVisaMobile = group.promoType === PROMOTION_TYPES.visamobile
  newGroup.isBlik = group.promoType === PROMOTION_TYPES.blik
  if (newGroup.isMastercard) {
    newGroup.iconUrl = '/web/img/icons/mastercard-visa.svg'
  }
  if (newGroup.isVisaMobile) {
    newGroup.iconUrl = '/web/img/icons/visa-mobile.svg'
  }
  return newGroup
}

/**
 * Metoda generująca listę kanałów płatności
 * @param state
 * @returns {*[]}
 */
/**
 * Wybiera regulamin kanału płatności w podanym języku.
 *
 * Backend przysyła `regulations` we WSZYSTKICH językach serwisu - po jednym wpisie na język, każdy
 * z własnym `regulationId` (pomiar 26.08.2026, testpay, kanał PIS 1897: 20 `regulationIds` na jeden
 * `gatewayId`). Dlatego wybór jest funkcją języka i musi być powtórzony przy jego zmianie -
 * `refreshActiveRegulationLanguage`. Wynik trafia do `state.form`, które w całości idzie do
 * `/update-payway`, więc przestawienie języka zmienia też `regulationAcceptanceId`: backend zapisuje
 * akceptację dokumentu, który użytkownik naprawdę widział.
 *
 * Jeden `regulationId` na kanale znaczy, że serwis ma tylko jeden wariant - wtedy język nie jest
 * kryterium, bo nie ma z czego wybierać (i nie ma czego odświeżać).
 *
 * @param {object} state
 * @param {object} payway
 * @param {string} lang
 * @returns {object|null}
 */
const findRegulationForPayway = (state, payway, lang) => {
  const regulationIds = payway?.regulationIds
  if (!regulationIds || regulationIds.length === 0 || !state.paymentData?.regulations) {
    return null
  }
  const regulations = state.paymentData.regulations
  if (regulationIds.length === 1) {
    return (
      regulations.find(
        (regulation) => regulation.regulationId === regulationIds[0] && regulation.gatewayId === payway.id
      ) ??
      regulations.find((regulation) => regulation.regulationId === regulationIds[0]) ??
      null
    )
  }
  const langUpper = lang.toUpperCase()
  return (
    regulations.find(
      (regulation) =>
        regulationIds.includes(regulation.regulationId) &&
        regulation.gatewayId === payway.id &&
        regulation.language === langUpper
    ) ??
    regulations.find(
      (regulation) => regulationIds.includes(regulation.regulationId) && regulation.language === langUpper
    ) ??
    null
  )
}

const generatePaywayList = (state) => {
  let paywayList = []

  if (!state.paymentData || !state.paymentData.paywayGroups) {
    return paywayList
  }

  state.paymentData.paywayGroups.forEach((group) => {
    paywayList = paywayList.concat(group.payways)
  })
  return paywayList
}

/**
 * Transakcje, dla których brak grup został już zgłoszony.
 *
 * `getPaymentGroupList` jest getterem Vuex czytanym z kilku computed, więc bez tego jedna zepsuta
 * odpowiedź API generowałaby serię identycznych zdarzeń. Klucz to `apiToken`, bo pytanie brzmi
 * „ile transakcji dostało dane bez grup", a nie „ile razy przeliczył się getter".
 */
const reportedMissingPaywayGroups = new Set()

/**
 * Metoda zgłaszająca odpowiedź API bez `paywayGroups`.
 *
 * Po dołożeniu guardu ten stan przestał rzucać, czyli **przestał być widoczny w Sentry sam z siebie**.
 * Bez tego raportu naprawa zamieniłaby głośną awarię w cichy paywall bez kanałów płatności.
 *
 * Przyczyna ustalona 25.08.2026: merchant inicjuje transakcję na **0 zł**, a konfiguracja jego
 * `serviceId` nie ma grupy płatności zdolnej obsłużyć taką kwotę. Backend nie blokuje rejestracji
 * i oddaje `status: SUCCESS` z `clientSource: 'payways'`, ale bez `paywayGroups`. Czyli **to nie defekt
 * bramki** - komunikat „Niedostępny kanał płatności" jest tu poprawny, poprawka należy do merchanta.
 *
 * ⚠️ Dlatego **kwota zerowa nie jest raportowana** (25.08.2026). Przez pierwsze dwa tygodnie raport
 * mierzył skalę zjawiska - 552 zdarzenia z kwotą, **550 z nich zerowe**, ani jednego z kwotą koszykową
 * (pomiar: `doc/robocze/sentry-pomiary.md`). Skala jest znana, a dalsze zgłaszanie tego samego
 * przypadku mierzyłoby cudzą pomyłkę konfiguracyjną i przy ~52 zdarzeniach/dobę zasypywałoby jedyny
 * sygnał, który jest tu naprawdę interesujący: **brak grup przy kwocie różnej od zera**. Tego
 * wyjaśnienie merchanta NIE obejmuje - takie zdarzenia były (dwa na `0.06`) i to one oznaczałyby
 * albo nieznaną ścieżkę, albo naszą regresję. Cisza dotyczy **wyłącznie** dokładnego zera; brak kwoty
 * (`null`/`undefined`) idzie do Sentry, bo „nie wiem, ile" to nie to samo co „wiem, że zero".
 * → `doc/dokumentacja/sentry.md` §5
 */
function reportMissingPaywayGroups(state) {
  const payAmount = state.paymentData?.payAmount ?? null
  if (payAmount !== null && Number(payAmount) === 0) {
    return
  }

  const apiToken = state.apiToken ?? null
  const key = String(apiToken)
  if (reportedMissingPaywayGroups.has(key)) {
    return
  }
  reportedMissingPaywayGroups.add(key)
  reportMessage('Paywall: odpowiedź API bez paywayGroups', {
    level: REPORT_LEVEL.warning,
    tags: { flow: 'payway-groups' },
    extra: {
      apiToken,
      serviceId: state.paymentData?.serviceID ?? null,
      transRemoteID: state.paymentData?.transRemoteID ?? null,
      czyPaymentData: !!state.paymentData,
      paywallMode: state.paywallMode ?? null,
      // Kwota zostaje w zgłoszeniu, choć zero jest odsiane wyżej: rozstrzyga, czy zgłoszenie dotyczy
      // kwoty dodatniej (nieznana ścieżka) czy braku danych (`null`). `clientSource` pokazuje, czy
      // backend mimo braku grup każe renderować paywall.
      payAmount,
      clientSource: state.paymentData?.clientSource ?? null,
    },
  })
}

/**
 * Metoda generująca listę grup płatności wraz z kanałami płatności dla paywalla na podstawie Gateway Listy 3.0
 * @param state
 * @param getters
 * @returns {*}
 */
const generatePaymentGroupsForGL30Response = (state, getters) => {
  let paywayGroups
  const result = []

  // Odpowiedź bez `paywayGroups`. Bliźniacze `generatePaywayList` ma ten guard od zawsze, tutaj go
  // brakowało - i to była przyczyna ~800 zdarzeń u ~700 użytkowników na dobę (pomiar 10-11.08.2026).
  //
  // Jeden brak guardu dawał DWA różne błędy: `apiGroups.forEach` rzucało przy pierwszym odczycie
  // gettera, a Vue zapamiętywało wtedy w computed `undefined` - więc kolejny odczyt
  // `paywayGroups.value` w `Paywall.vue` trafiał na `.find` po `undefined`. Zmierzone na produkcji:
  // ta sama sesja (jeden użytkownik, jeden `apiToken`, Samsung Internet) zgłosiła
  // `reading 'forEach'` o 12:14:10.560 i `reading 'find'` o 12:14:10.574 - **14 ms odstępu**.
  // Stąd dwie rodziny zgłoszeń o tym samym komponencie `<PaymentPaywall>`, każda dodatkowo rozbita
  // na 8-9 grup przez hashowane nazwy chunków. Szczegóły: `doc/dokumentacja/sentry.md` §5.
  if (!state.paymentData || !state.paymentData.paywayGroups) {
    reportMissingPaywayGroups(state)
    return []
  }

  const apiGroups = state.paymentData.paywayGroups
  const frontGroups = paywayGroupsList()

  apiGroups.forEach((group) => {
    const apiFrontType = PAYWAY_GROUPS_FRONT_API_MAP[group.type]
    if (apiFrontType === PAYWAY_GROUPS_TYPES.promo) {
      const promoGroup = generatePromoGroup(getters, apiFrontType, group, state)
      result.push(Object.assign(promoGroup, group))
    } else {
      const findedGroup = frontGroups.find((frontGroup) => frontGroup.type === apiFrontType)
      if (findedGroup) {
        const newGroup = Object.assign(findedGroup, group)
        newGroup.type = apiFrontType
        result.push(newGroup)
      }
    }
  })
  paywayGroups = getNotEmptyPaywayGroups(result)

  // payment fee - sterowanie grupa BLIK na podstawie logiki prowizji
  if (
    isPaymentFeeActive({ paymentData: state.paymentData }) &&
    PAYMENT_FEE_SID_CONFIG.includes(Number(state.paymentData.serviceID))
  ) {
    paywayGroups = sortPaymentGroups(paywayGroups)
  }

  // payment boost - sterowanie grupami na podstawie logiki
  if (PaymentBoost.isPaymentBoostActive(state.paymentData.serviceID, state.paymentData.transaction.payAmount)) {
    paywayGroups = PaymentBoost.sortPaymentGroups(paywayGroups)
  }

  // PEP-1298 - kolejność portfeli wg urządzenia, w wariancie testowym eksperymentu Matomo.
  // Po PaymentBoost, bo boost ustawia oba portfele w sztywnej sekwencji i nadpisałby zamianę.
  if (state.walletOrderVariant === WALLET_ORDER_VARIANT.byDevice) {
    paywayGroups = orderWalletGroups(paywayGroups, getPreferredWalletGroupType())
  }

  // wybrany kanał płatności
  if (state.paywallMode === PAYWALL_MODE.chosenPayway) {
    return paywayGroups.filter((group) => {
      const choosenPayway = group.payways.find((payway) => payway.id === state.paymentData.chosenPaywayId)
      if (choosenPayway) {
        group.payways = [choosenPayway]
        return true
      }
      return false
    })
  }

  return paywayGroups
}
